import React, { FC } from 'react';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

interface AvailableLanguage {
  href: string;
  label: string;
}

interface Props {
  availableLanguages: AvailableLanguage[];
}

const sections = [
  { title: 'Descripción general' },
  { title: 'Información que podemos recopilar' },
  { title: 'Divulgación e información biométrica' },
  {
    title:
      'Cómo podemos utilizar su Información personal para proporcionar nuestros Servicios de IDV a los Clientes',
  },
  {
    title:
      'Cómo podemos utilizar información anonimizada o agregada para mantener, mejorar y desarrollar los Servicios de IDV',
  },
  { title: 'Sus derechos y opciones' },
  { title: 'Seguridad y retención' },
  { title: 'Actualizaciones de esta Declaración de privacidad de IDV' },
  { title: 'Contacto con Plaid' },
  { title: 'Para residentes de la Unión Europea y el Reino Unido' },
];

const EsVersion: FC<Props> = ({ availableLanguages }) => {
  return (
    <>
      <h4 id='declaracion-de-privacidad-de-idv'>
        Declaración de privacidad de IDV
      </h4>

      <p>Idiomas disponibles:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Última actualización: 20 de febrero de 2026</p>

      <p>
        Plaid Inc. y sus subsidiarias, Plaid Financial Ltd. y Plaid, B.V. (en
        conjunto, "Plaid", "nosotros", "nuestro" o "nos") ofrecen servicios de
        verificación de identidad, detección de fraude y filtrado de listas de
        vigilancia (los "Servicios de IDV" o "Servicios") a nivel mundial a
        compañías, empresas e instituciones financieras ("Clientes" o
        "Cliente").
      </p>

      <p>
        Es posible que uno de nuestros Clientes le pida que complete varios
        controles utilizando los Servicios de IDV. Esta declaración de
        privacidad de IDV ("Declaración de privacidad de IDV") explica cómo se
        pueden utilizar sus datos personales ("Información personal") cuando
        proporcionamos los Servicios de IDV en nombre de nuestros Clientes.
      </p>

      <p>
        Plaid no recopila, procesa, utiliza ni comparte intencionalmente
        Información personal para consumidores menores de 18 años. Los Servicios
        de IDV están destinados únicamente a personas mayores de 18 años. Si es
        menor de 18 años, no utilice los Servicios de IDV.
      </p>

      <p>
        Cuando prestamos Servicios de IDV a nuestros Clientes, nuestros Clientes
        determinan el fin y los medios por los que se procesa su Información
        personal (y nuestros Clientes son considerados controladores de datos).
        Actuamos según las instrucciones de nuestros Clientes con respecto a
        cómo y por qué se procesa su Información personal. Plaid es un
        procesador o subprocesador de datos cuando facilita los servicios de
        verificación de identidad, detección de fraude y filtrado de listas de
        vigilancia bajo la dirección de nuestros Clientes. Nuestros Clientes nos
        indican que recopilemos su Información personal. Plaid actúa como
        procesador o subprocesador cuando se le indica que recopile información
        sobre usted, incluida su Información personal, y Plaid recibe
        instrucciones de nuestros clientes sobre de quién recopilar datos
        personales y sobre cuándo recopilar y procesar su Información personal.
        Utilizamos su Información personal para proporcionar Servicios de IDV a
        nuestros Clientes. También transferimos o proporcionamos acceso para
        descargar la información que recopilamos de usted, incluida su
        Información personal, a los Clientes bajo su instrucción y solicitud.
      </p>

      <p>
        Siempre debe consultar las políticas de privacidad de nuestros Clientes
        y dirigirles cualquier consulta y solicitud para ejercer sus derechos
        con respecto a su Información personal.
      </p>

      <p>
        Es posible que usted acceda directamente a algunos de los Servicios de
        IDV, incluso a través de nuestros sitios web que hacen referencia a esta
        política (colectivamente, "Sitios"). En algunos casos, por ejemplo, si
        usted es un Cliente o si desea realizar una consulta sobre los Servicios
        de IDV, o si visita nuestro sitio web, podemos ser un controlador de
        datos. Describimos cómo y por qué utilizamos los datos personales en
        estas circunstancias en las políticas de privacidad aplicables de Plaid
        en{' '}
        <Link href='https://plaid.com/legal/'>
          <a>www.plaid.com/legal/</a>
        </Link>
        .
      </p>

      <p>
        Plaid nunca venderá su Información personal de los Servicios de IDV a
        terceros. Nuestros Clientes deciden cómo utilizamos su Información
        personal, controlan sus políticas de retención de datos y pueden
        especificar durante cuánto tiempo conservar su Información personal y si
        compartimos la información que recopilamos, incluida su Información
        personal, con nuestro Cliente y cuándo la compartimos.
      </p>

      <p>
        Dependiendo del servicio requerido por nuestros Clientes, podemos
        procesar su Información personal en nombre de nuestros Clientes como se
        describe en esta Declaración de privacidad, por lo que le recomendamos
        que la lea detenidamente.
      </p>

      <nav>
        <h5>Saltar a la sección:</h5>
        <ol>
          {sections.map((section, index) => {
            return (
              <li key={index}>
                <a href={`#section-es-${index + 1}`}>{`${section.title}`}</a>
              </li>
            );
          })}
        </ol>
      </nav>

      <div id='section-es-1'>
        <h5>1. Descripción general</h5>
        <p>
          Cuando Plaid preste Servicios de IDV a los Clientes, se le podría
          pedir que nos proporcione información sobre usted. En nombre de
          nuestros Clientes, proporcionamos servicios de verificación de
          identidad, detección de fraude y filtrado de listas de vigilancia
          basados en la nube. Los Servicios de IDV recopilan información de
          usted y sobre usted para realizar predicciones y ayudar a prevenir
          actividades fraudulentas en tiempo real. Luego Plaid completa nuestros
          Servicios de IDV y proporciona los resultados directamente a nuestros
          Clientes. A petición del Cliente, proporcionamos una copia de la
          información que usted comparte, o la transferimos, incluida su
          Información personal, para el uso del Cliente y como parte de los
          Servicios de IDV. Podemos transferir a los Clientes, o proporcionarles
          acceso para que descarguen, la información que recopilamos de usted,
          incluida su Información personal, bajo las instrucciones de nuestros
          Clientes.
        </p>
        <p>
          Nuestros Clientes determinan el alcance de la solicitud de información
          y qué información personal sobre usted podría utilizarse para el
          análisis con los Servicios de IDV. Los Clientes configuran los
          Servicios de IDV para que requiera y utilice cierta información para
          verificar su identidad. Un Cliente puede configurar los Servicios de
          IDV para solicitar información limitada. Si la información solicitada
          no es suficiente para proporcionar una verificación, los Servicios de
          IDV pueden solicitar información adicional, según las instrucciones
          del Cliente, para completar el proceso de verificación.
        </p>
        <p>
          Si no está satisfecho con el resultado de cualquier procesamiento,
          debe dirigir su consulta al Cliente.
        </p>
      </div>

      <div id='section-es-2'>
        <h5>2. Información que podemos recopilar</h5>
        <p>
          Dependiendo de los Servicios de IDV solicitados por nuestros Clientes,
          podemos recopilar su Información personal de las siguientes maneras:
        </p>
        <p>
          <strong>Información que usted proporciona:</strong> Recopilamos su
          Información personal cuando usted nos proporciona la información
          solicitada por el Cliente. Si decide no proporcionar la información
          solicitada, no podremos proporcionar a nuestro Cliente los Servicios
          de IDV. Póngase en contacto con el Cliente para analizar opciones
          alternativas de verificación de identidad y filtrado, si estas están
          disponibles para su región o país.
        </p>
        <p>
          Parte de la información potencial sobre usted que puede solicitarse en
          nombre de nuestros Clientes incluye detalles de contacto, incluidos su
          nombre, dirección de correo electrónico, dirección postal, número de
          teléfono, fecha de nacimiento; documentos de identidad con su imagen;
          su número de identidad asignado por el gobierno, como un número de
          seguro social; y/o una fotografía o imagen de video de usted, para
          evaluar si una persona coincide con su documento de identidad y parece
          ser una persona en vivo.
        </p>
        <p>
          Recopilamos datos directamente de usted a través de técnicas estándar
          del sector como código JavaScript, kits de desarrollo de software
          (Software Development Kit, SDK), envíos de formularios, recopilación
          de imágenes, recopilación de videos; y conductas al utilizar
          herramientas que nuestros Clientes integran en sus sitios web o
          aplicaciones móviles (<strong>"Sitios del Cliente"</strong>).
        </p>
        <p>
          <strong>
            Información utilizada para verificar un número de teléfono
          </strong>
          : también podemos proporcionar una función de autenticación de dos
          factores a nuestros Clientes para ayudar a proporcionar medidas a fin
          de garantizar que la persona que proporciona la información tenga el
          número de teléfono asociado con esa identidad. Para proporcionar esta
          función a nuestros Clientes, podemos utilizar su Información personal
          proporcionada por nuestro Cliente o por usted para enviarle códigos de
          verificación, como por ejemplo a través de mensajes de texto, que
          usted luego puede introducir para confirmar su identidad cuando
          utilice un Sitio del Cliente o al crear una nueva cuenta con un
          Cliente.
        </p>
        <p>
          <strong>
            Información que recopilamos automáticamente cuando visita los Sitios
            del Cliente:
          </strong>{' '}
          nuestros Servicios de IDV utilizan ciertas tecnologías de seguimiento
          estándar para recopilar automáticamente cierta información sobre su
          dispositivo cuando interactúa con, y utiliza, los Sitios del Cliente,
          incluyendo análisis de comportamiento con respecto a su interacción
          con los Servicios de IDV y la frecuencia con la que se utiliza un
          dispositivo para interactuar con los Sitios del Cliente y los
          Servicios de IDV. Parte de esta información incluye, por ejemplo, su
          dirección IP y ciertos identificadores únicos, y puede identificar un
          equipo o dispositivo en particular y puede considerarse Información
          personal en algunas jurisdicciones, incluida la UE.
        </p>
        <p>
          Los tipos de información que podemos recopilar en nombre de nuestros
          Clientes dependerán de si usted visita un Sitio del Cliente a través
          de una interfaz de programación de aplicaciones (Application
          Programming Interface, API), una aplicación que utilice nuestro SDK o
          una página web, lo que incluye:
        </p>
        <ul>
          <li>
            <strong>Información del navegador y del dispositivo</strong>,
            incluidos el tipo y el modelo del dispositivo; el fabricante; el
            tipo y la versión del sistema operativo; el tipo y la versión del
            navegador web; el agente de usuario; el nombre y la información del
            operador telefónico; la zona horaria; el tipo de conexión de red; la
            dirección IP; los identificadores basados en hardware; el nombre del
            host; los identificadores del dispositivo; la ID de publicidad de
            Android/Google; las huellas dactilares del lienzo; las
            características relacionadas con equipos emulados o con
            modificaciones no autorizadas, como si su dispositivo está
            "liberado"; y el nombre y la versión de la aplicación. También
            recopilamos el conjunto de caracteres, el nombre del host, el
            idioma, el título y la URL de la página, la URL del remitente, la
            cantidad de fuentes, el hash de fuentes, la cantidad de plugins, el
            hash de plugins, el alto y ancho de la pantalla, la profundidad del
            color, la plataforma, la huella de cookies, los puntos de contacto
            máximos, la habilitación para Java, el almacenamiento de sesiones,
            el almacenamiento local, si la resolución se ha manipulado, el
            idioma o sistema operativo, si el bloqueo de anuncios está activado,
            si no rastrear está activado, la IP del puerto para flash y el
            identificador de flash. El SDK también recopilará metadatos
            relacionados con el teléfono, como el nivel de batería, las
            propiedades del dispositivo y el nombre del operador.
          </li>
          <li>
            <strong>
              Información sobre sus conductas en los Sitios del Cliente
            </strong>
            , es decir, información sobre sus actividades en los Sitios del
            Cliente; la ID de sesión; la hora de inicio y finalización de la
            sesión; la diferencia de zona horaria y la información de ubicación,
            que puede ser información de ubicación general inferida de su
            dirección IP o, en algunas circunstancias, información de
            geolocalización más precisa basada en coordenadas de latitud y
            longitud. Es posible que pueda controlar la recopilación de
            información de ubicación a través de Sitios del Cliente en
            particular cambiando las preferencias en su dispositivo móvil.
          </li>
        </ul>
        <p>
          <strong>
            Cómo utilizamos las tecnologías de seguimiento para recopilar
            información sobre usted.
          </strong>{' '}
          Utilizamos tecnologías de seguimiento estándar para recopilar
          automáticamente cierta información de su dispositivo y navegador
          cuando visita los Sitios del Cliente o interactúa con estos. Cuando ve
          o utiliza los Servicios de IDV a través de un Sitio del Cliente, se
          notifica a los servidores de Plaid y podemos recopilar información del
          navegador o la aplicación. Además, recopilamos datos directamente de
          usted a través de tecnologías de seguimiento estándar como JavaScript
          o SDK, que nuestros Clientes pueden integrar en sus sitios web.
        </p>
        <p>
          Los tipos de tecnologías de seguimiento que podemos utilizar incluyen:
        </p>
        <ul>
          <li>
            El <strong>código JavaScript (JS Snippet)</strong>, también llamado
            etiqueta o píxel, es un pequeño fragmento de código insertado en el
            contenido del Sitio del Cliente. Puede desactivar JavaScript al
            cambiar la configuración de su navegador. La información sobre cómo
            cambiar esa configuración se puede encontrar generalmente en el
            sitio web de su proveedor de navegador de Internet. Dado que Plaid
            no controla esta configuración, le recomendamos que consulte la
            información proporcionada de forma regular para asegurarse de que
            conoce cualquier cambio relevante.
          </li>
          <li>
            <strong>
              Kits de desarrollo de software (Software Development Kit, SDK)
              móviles
            </strong>
            , o bloques de código integrados en el sitio de un Cliente que
            permiten a Plaid recopilar cierta información. Puede controlar el
            uso de cierta información que Plaid recopila a través de los SDK
            siguiendo las instrucciones aplicables al sistema operativo de su
            dispositivo móvil. Es posible que también pueda controlar si su
            dispositivo móvil proporciona información de ubicación en Sitios del
            Cliente en particular, y cómo lo hace, cambiando las preferencias en
            su dispositivo móvil. Dado que Plaid no controla esta configuración,
            le recomendamos que consulte la información proporcionada de forma
            regular para asegurarse de que conoce cualquier cambio relevante.
          </li>
          <li>
            <strong>Toma de huellas dactilares del lienzo</strong>, una técnica
            de seguimiento que nos permite renderizar imágenes gráficas a partir
            de funciones integradas de un lienzo de HTML5 en su navegador. La
            imagen del lienzo suele representarse de forma diferente en
            diferentes dispositivos debido a una serie de factores; como la
            versión de su navegador web, el sistema operativo y su
            configuración, y el hardware de gráficos instalado; y nos permite
            distinguirlo de otros usuarios. Si desea deshabilitar la toma de
            huellas dactilares del lienzo, puede optar por deshabilitar
            JavaScript o descargar una extensión del navegador que bloquee las
            técnicas de toma de huellas dactilares del lienzo, pero hacerlo
            puede afectar la funcionalidad de los Servicios de IDV. Dado que
            Plaid no controla esta configuración ni estas extensiones, le
            recomendamos que consulte la información proporcionada sobre estos
            de forma regular para asegurarse de que conoce cualquier cambio
            relevante.
          </li>
          <li>
            <strong>Huellas dactilares del dispositivo</strong>, una técnica de
            seguimiento que nos permite identificarlo utilizando información
            relacionada con el software y hardware de su dispositivo, incluidos
            los componentes de su huella dactilar del lienzo, en combinación con
            la información de su dispositivo. Plaid utiliza la huella dactilar
            del dispositivo para detener el fraude y otros malhechores, así como
            para la ciberseguridad.
          </li>
        </ul>
        <p>
          Parte de esta información, como su dirección IP y ciertos
          identificadores únicos, podrían identificar un equipo o dispositivo en
          particular, y por lo tanto podría considerarse "Información personal"
          en algunas jurisdicciones, incluida la UE y California.
        </p>
        <p>
          <strong>
            Información que recopilamos de proveedores de servicios y fuentes de
            terceros:
          </strong>{' '}
          A petición de nuestro Cliente y como parte de los Servicios de IDV
          para Clientes, podemos combinar la información que recopilamos sobre
          usted con la información que recibimos de proveedores de servicios.
          Por ejemplo, podemos recibir información como si una dirección IP es
          comercial o privada, si un número de teléfono es una línea fija o si
          un dominio de correo electrónico es gratuito. Los clientes también
          pueden solicitarnos que trabajemos con terceros que cotejen la
          información proporcionada con fuentes de terceros, como archivos de
          datos de deudores, registros de cuentas de teléfonos móviles,
          registros de servicios públicos, registros de vehículos motorizados y
          otras fuentes de confianza. Para ciertos países, es posible que se le
          pida a su proveedor de telefonía móvil que divulgue los detalles de su
          cuenta móvil con el fin de verificar su identidad, lo que incluye su
          nombre, su dirección y los detalles de su dispositivo. También podemos
          recibir información sobre usted de terceros o recopilar información
          sobre usted que esté disponible públicamente, en nombre de nuestro
          Cliente.
        </p>
        <p>
          <strong>Categorías especiales de datos personales.</strong> Si
          nuestros Clientes requieren que usted nos proporcione un documento que
          contenga su fotografía o si necesita verificar su identidad
          proporcionando una fotografía o imagen de video de usted, estas
          imágenes pueden revelar categorías especiales de datos sobre usted;
          por ejemplo, información relacionada con su salud (por ejemplo, si usa
          anteojos), con su raza o etnia, o con sus creencias religiosas o
          políticas. Utilizamos estas categorías especiales de datos personales
          solo según las instrucciones de nuestro Cliente y de acuerdo con
          cualquiera de las políticas de privacidad de nuestros Clientes y esta
          Declaración de privacidad de IDV, y por ningún otro motivo. IDV de
          Plaid no vende Información personal.
        </p>
      </div>

      <div id='section-es-3'>
        <h5>3. Divulgación e información biométrica</h5>
        <p>
          Si nuestro Cliente requiere que nos proporcione un documento de
          identidad que contenga una imagen de su rostro y una fotografía o un
          video (a veces denominada selfie), podemos comparar su geometría
          facial derivada de la imagen en su documento de identidad con su
          geometría facial derivada en la fotografía o imagen de video que usted
          proporcione para determinar si una persona coincide con su documento
          de identidad. También podemos usar su escaneo de geometría facial para
          detectar y prevenir el fraude, incluso para determinar si la imagen de
          video es de una persona en vivo y/o determinar su edad estimada, si
          corresponde. La imagen, fotografía o imagen de video de su documento
          de identidad, y cualquier escaneo de geometría facial derivado de esas
          imágenes, pueden considerarse información biométrica en algunas
          jurisdicciones.
        </p>
        <p>
          Al utilizar los Servicios de IDV para conectarse a una aplicación o un
          servicio proporcionado por nuestro Cliente, usted acepta que ha leído,
          comprende y acepta voluntariamente{' '}
          <Link href='#biometric-policy-and-release'>
            <a>la Política y exención de información biométrica de Plaid</a>
          </Link>
          , y exime a favor de Plaid y los proveedores de servicios de Plaid
          cualquier reclamación relacionada con la recopilación, el
          procesamiento, el uso, la divulgación o la compartición de sus
          imágenes, fotografías o imágenes de video, incluidos los datos
          derivados de esas imágenes para crear escaneos de geometría facial.
        </p>
        <p>
          Para obtener más información sobre nuestra recopilación, uso,
          procesamiento, divulgación y retención de su imagen a partir de su
          documento de identidad, fotografía o imagen de video, incluidos los
          escaneos de geometría facial derivados de esas imágenes que pueden
          considerarse datos biométricos en su región, consulte{' '}
          <Link href='#biometric-policy-and-release'>
            <a>la Política y exención de información biométrica de Plaid</a>
          </Link>
          .
        </p>
      </div>

      <div id='section-es-4'>
        <h5>
          4. Cómo podemos utilizar su Información personal para proporcionar
          nuestros Servicios de IDV a los Clientes
        </h5>
        <p>
          Nuestros Clientes pueden utilizar nuestros Servicios de IDV y nosotros
          devolvemos información al Cliente sobre la probabilidad de que la
          persona que proporciona la información sea quien afirma ser. También
          les transferimos a los Clientes, o les proporcionamos acceso para que
          descarguen, la información que recopilamos de usted, incluida su
          Información personal, bajo la instrucción y solicitud de los Clientes.
        </p>
        <p>
          Plaid utiliza su Información personal para proporcionar los Servicios
          de IDV a nuestros Clientes y para ayudar a nuestros Clientes a cumplir
          con sus obligaciones legales. También podemos utilizar la información
          del dispositivo o las tecnologías de seguimiento para identificar y
          prevenir el fraude en los Servicios de IDV. Por ejemplo, procesamos Su
          Información personal a través de nuestro servicio de verificación de
          identidad basado en la nube para devolver información de coincidencia
          a nuestros Clientes para eventos o actividades particulares en el
          Sitio del Cliente. Además, cuando nuestros Clientes utilizan la
          autenticación de dos factores, procesamos su Información personal,
          como su número de teléfono, para enviarle un código de verificación
          por mensaje de texto. Esto ayuda a nuestros Clientes que utilizan esta
          función para verificar las identidades al asegurar la posesión del
          número de teléfono asociado con las identidades.
        </p>
      </div>

      <div id='section-es-5'>
        <h5>
          5. Cómo podemos utilizar información anonimizada o agregada para
          mantener, mejorar y desarrollar los Servicios de IDV
        </h5>
        <p>
          Podemos anonimizar, despersonalizar o agregar información para
          mantener, mejorar y optimizar o desarrollar los Servicios de IDV.
          También podemos usar información anonimizada, despersonalizada o
          agregada para prevenir y detectar fraudes en los Servicios de IDV. IDV
          de Plaid nunca utiliza su Información personal para mantener, mejorar,
          optimizar o desarrollar los Servicios de IDV.
        </p>
      </div>

      <div id='section-es-6'>
        <h5>6. Sus derechos y opciones</h5>
        <p>
          Dependiendo de su ubicación y sujeto a la legislación aplicable, usted
          puede tener los derechos con respecto a su Información personal que
          procesamos en nombre del Cliente.
        </p>
        <p>
          <strong>Derechos de protección de datos.</strong> Cuando actuemos como
          procesador o subprocesador de datos, o proveedor de servicios, en
          nombre de nuestros Clientes, usted debe dirigir al Cliente cualquier
          solicitud para acceder a su Información personal o ejercer cualquiera
          de sus derechos de protección de datos. Ayudaremos a nuestros Clientes
          a responder a su solicitud y actuaremos según las instrucciones que
          recibamos de ellos.
        </p>
        <p>
          Consulte la Política de privacidad del usuario final de Plaid en{' '}
          <Link href='https://plaid.com/legal/#consumers'>
            <a>https://plaid.com/legal/#consumers</a>
          </Link>{' '}
          para obtener más detalles sobre cómo abordamos cualquier solicitud en
          la que somos responsables de procesar cualquier dato personal como
          controlador de datos.
        </p>
      </div>

      <div id='section-es-7'>
        <h5>7. Seguridad y retención</h5>
        <p>
          <strong>Protecciones de seguridad.</strong> Utilizamos medidas de
          seguridad técnicas y organizativas diseñadas para proteger la
          Información personal procesada como parte de los Servicios de IDV
          contra el acceso no autorizado, la divulgación, la alteración y la
          destrucción.
        </p>
        <p>
          <strong>Retención de datos.</strong> Los Clientes controlan sus
          políticas de retención de datos y pueden especificar cuánto tiempo
          deben conservar su Información personal. Cuando un Cliente así lo
          indique, su Información personal será eliminada o anonimizada.
        </p>
      </div>

      <div id='section-es-8'>
        <h5>8. Actualizaciones de esta Declaración de privacidad de IDV</h5>
        <p>
          Podemos revisar la Declaración de privacidad de IDV de vez en cuando
          en respuesta a los requisitos de los Clientes en relación con nuestros
          Servicios y los cambios en los desarrollos legales, técnicos o
          comerciales. Proporcionaremos cualquier actualización en nuestro Sitio
          y la versión revisada entrará en vigor cuando se publique. Si
          realizamos cambios considerables en las formas en que utilizamos o
          compartimos la Información Personal recopilada previamente de usted,
          publicaremos la versión actualizada aquí. Puede ver cuándo se
          actualizó por última vez esta Declaración de privacidad de IDV
          controlando la fecha "last updated" ("última actualización") o
          "effective" ("vigente") que se muestra en la parte superior de esta
          página.
        </p>
      </div>

      <div id='section-es-9'>
        <h5>9. Contacto con Plaid</h5>
        <p>
          Póngase en contacto con Plaid si tiene alguna pregunta o comentario
          sobre esta Declaración de privacidad de IDV o nuestras prácticas de
          privacidad escribiendo a{' '}
          <Link href='mailto:privacy@plaid.com'>
            <a>privacy@plaid.com</a>
          </Link>{' '}
          o por correo postal a:
        </p>
        <p>
          Plaid Inc.
          <br />
          Attn: Legal
          <br />
          PO Box 7775 #35278
          <br />
          San Francisco, California 94120-7775
          <br />
          EE. UU.
        </p>
      </div>

      <div id='section-es-10'>
        <h5>10. Para residentes de la Unión Europea y el Reino Unido</h5>
        <p>
          Plaid ha nombrado a Plaid B.V. como su representante de protección de
          datos a los efectos del Reglamento General de Protección de Datos
          (RGPD) en la UE/EEE y la Ley de protección de datos de 2018 (con sus
          enmiendas) en el Reino Unido. Si usted es residente de la UE/EEE o del
          Reino Unido y hemos procesado o estamos procesando Su Información
          personal, puede tener derecho a ejercer sus derechos en virtud del
          RGPD.
        </p>
        <p>
          Si nuestro Cliente en cuyo nombre hemos tratado su Información
          personal también se encuentra en el EEE o el Reino Unido, debe ponerse
          en contacto con nuestro Cliente sobre su Información personal en lugar
          de con el representante de protección de datos de Plaid. Si aún desea
          plantear una pregunta a Plaid, o ejercer de otro modo sus derechos con
          respecto a su Información personal, puede hacerlo:
        </p>
        <ul>
          <li>al enviar un correo electrónico a privacy@plaid.com; o</li>
          <li>
            al comunicarse con nuestro representante de protección de datos a
            través del formulario web en{' '}
            <Link href='https://plaid.com/legal/data-protection-request-form/'>
              <a>https://plaid.com/legal/data-protection-request-form/</a>
            </Link>
            .
          </li>
        </ul>
        <p>
          También puede enviar su consulta por correo postal a Plaid a las
          siguientes direcciones:
        </p>
        <p>
          <em>Si reside en el Reino Unido:</em>
        </p>
        <p>
          Plaid Financial Ltd.
          <br />
          Attn: Legal
          <br />
          Techspace Level 4, 25 Worship Street
          <br />
          Londres, Reino Unido, EC2A 2DX
        </p>
        <p>
          <em>Si reside en el EEE:</em>
        </p>
        <p>
          Plaid B.V.
          <br />
          Attn: Legal
          <br />
          Muiderstraat 1<br />
          1011PZ Ámsterdam
          <br />
          Países Bajos
        </p>
        <p>
          Al recibir su correspondencia, es probable que Plaid solicite pruebas
          de su identidad para garantizar que su Información personal no se
          proporcione a nadie que no sea usted.
        </p>
      </div>

      <hr className='section-divider' />
    </>
  );
};

export default EsVersion;
